/* ============================================================
   20260820 テーマ見直し — 共通カスタムスクリプト
   1. ヘッダーの表示制御（重ね表示／透過）
   2. スライドショーの高さ自動調整（画像を見切れさせない）
   3. メガメニューのホバー展開
   4. 商品カルーセルのナビゲーション矢印表示制御
   5. 商品カルーセルの「横に続きがある」ことを示す動き（20261014追加）
   ============================================================ */
(function () {
  'use strict';

  function throttled(fn) {
    var rafId = null;
    return function () {
      if (rafId) return;
      rafId = requestAnimationFrame(function () {
        rafId = null;
        fn();
      });
    };
  }

  /* 20260825修正: ヘッダーの挙動を以下のルールに変更。
     ・ヘッダーは常に表示する（スクロールで隠す挙動を撤去）
     ・スライドショーがあるページ（ホームページ）ではヘッダーを
       スライドショーに重ね、背後にスライドショーが見えている間は背景を透過
     ・スライドショーが見えなくなったら透過を解除
     ・PCのホバー時の透過解除は CSS 側（:hover）で制御
     ・スライドショーが無いページは従来どおり（sticky・非透過） */
  function initHeader() {
    var header = document.getElementById('shopify-section-header');
    if (!header) return;

    var hero = document.querySelector('[data-pl-hero]');
    var announcement = document.querySelector('.announcement-bar');
    var headerHeight = 0;

    function measure() {
      headerHeight = header.offsetHeight;
      document.documentElement.style.setProperty('--pl-header-height', headerHeight + 'px');
    }

    // スライドショーが無いページは重ね表示も透過もしない
    if (!hero) {
      measure();
      window.addEventListener('resize', measure);
      window.addEventListener('load', measure);
      return;
    }

    header.classList.add('pl-header--overlay');

    function update() {
      // アナウンスバーが画面上に残っている間は、その下にヘッダーを置く
      var top = 0;
      if (announcement) {
        var aRect = announcement.getBoundingClientRect();
        if (aRect.bottom > 0) top = aRect.bottom;
      }
      document.documentElement.style.setProperty('--pl-header-top', Math.round(top) + 'px');

      // スライドショーがヘッダーの背後にまだ見えている間だけ透過させる。
      // 最上部では必ず true、スライドショーをスクロールし過ぎると false になる。
      var heroRect = hero.getBoundingClientRect();
      var transparent = heroRect.bottom > top + headerHeight;
      header.classList.toggle('pl-header--transparent', transparent);
    }

    measure();
    update();

    var onScroll = throttled(update);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () {
      measure();
      update();
    });

    window.addEventListener('load', function () {
      measure();
      update();
    });
    window.setTimeout(function () { measure(); update(); }, 120);
    window.setTimeout(function () { measure(); update(); }, 600);

    if (window.ResizeObserver) {
      var ro = new ResizeObserver(function () {
        measure();
        update();
      });
      ro.observe(header);
      ro.observe(hero);
      if (announcement) ro.observe(announcement);
    }
  }

  /* 20260824修正: PC用／モバイル用で別々の画像（縦横比）を設定できる
     ようにしたため、高さ計算もビューポート幅に応じて
     data-pl-aspect（PC）/ data-pl-aspect-mobile（モバイル）を
     切り替えて参照するようにした。ブレークポイントは Dawn 標準に
     合わせて 750px とする。 */
  function initSlideshow() {
    var mobileQuery = window.matchMedia('(max-width: 749px)');

    document.querySelectorAll('slideshow-component').forEach(function (comp) {
      var slider = comp.querySelector('[id^="Slider-"]');
      if (!slider) return;

      var slides = Array.prototype.slice.call(slider.querySelectorAll('.slideshow__slide'));
      if (!slides.length) return;

      function ratioFor(slide) {
        var attr = mobileQuery.matches ? 'data-pl-aspect-mobile' : 'data-pl-aspect';
        var r = parseFloat(slide.getAttribute(attr));
        if (!r || r <= 0) {
          r = parseFloat(slide.getAttribute('data-pl-aspect'));
        }
        return r && r > 0 ? r : 16 / 9;
      }

      function apply() {
        var width = slider.clientWidth;
        if (!width) return;
        var index = Math.round(slider.scrollLeft / width);
        if (index < 0) index = 0;
        if (index > slides.length - 1) index = slides.length - 1;

        var ratio = ratioFor(slides[index]);
        var height = width / ratio;
        // 正方形など縦長の画像でヒーローが巨大にならないよう画面高さで頭打ちにする。
        // object-fit: contain なので、頭打ちになっても画像が見切れることはない。
        var maxHeight = Math.max(320, window.innerHeight * 0.86);
        if (height > maxHeight) height = maxHeight;
        slider.style.height = Math.round(height) + 'px';
      }

      var applyThrottled = throttled(apply);
      comp.addEventListener('slideChanged', apply);
      slider.addEventListener('scroll', applyThrottled, { passive: true });
      window.addEventListener('resize', applyThrottled);
      apply();
      // 初期レイアウト確定後にもう一度
      window.setTimeout(apply, 60);
      window.addEventListener('load', apply);
    });
  }

  function initNavFit() {
    var headerEl = document.querySelector('.header');
    if (!headerEl) return;

    var nav = headerEl.querySelector('.header__inline-menu');
    var logo = headerEl.querySelector('.header__heading-link');
    var icons = headerEl.querySelector('.header__icons');
    if (!nav || !logo) return;

    function fit() {
      if (window.innerWidth < 990) {
        headerEl.classList.remove('pl-nav-collapsed');
        return;
      }

      headerEl.classList.add('pl-nav-measuring');
      var needed = nav.offsetWidth;
      headerEl.classList.remove('pl-nav-measuring');

      var iconsWidth = icons ? icons.offsetWidth : 0;

      var cs = window.getComputedStyle(headerEl);
      var contentWidth =
        headerEl.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      var gap = parseFloat(cs.columnGap) || 0;
      var sideWidth = (contentWidth - logo.offsetWidth - gap * 2) / 2 - 4;

      headerEl.classList.toggle(
        'pl-nav-collapsed',
        needed > sideWidth || iconsWidth > sideWidth
      );
    }

    fit();
    window.addEventListener('resize', fit);
    window.addEventListener('load', fit);
    window.setTimeout(fit, 200);
    window.setTimeout(fit, 800);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
  }

  function initMegaMenu() {
    var nav = document.querySelector('.header__inline-menu');
    if (!nav) return;

    var mq = window.matchMedia('(min-width: 990px) and (hover: hover)');

    var CLOSE_DELAY = 260;
    var closeTimer = null;

    function cancelClose() {
      if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
    }

    function closeDetails(details) {
      details.removeAttribute('open');
      var s = details.querySelector('summary');
      if (s) s.setAttribute('aria-expanded', 'false');
    }

    function scheduleClose(details) {
      cancelClose();
      closeTimer = window.setTimeout(function () {
        closeTimer = null;
        closeDetails(details);
      }, CLOSE_DELAY);
    }

    function closeAll(except) {
      nav.querySelectorAll('details[open]').forEach(function (d) {
        if (d === except) return;
        closeDetails(d);
      });
    }

    nav.querySelectorAll('details-disclosure').forEach(function (disclosure) {
      var details = disclosure.querySelector('details');
      var summary = details && details.querySelector('summary');
      if (!details || !summary) return;
      var panel = details.querySelector('.mega-menu');

      function open() {
        if (!mq.matches) return;
        cancelClose();
        closeAll(details);
        details.setAttribute('open', '');
        summary.setAttribute('aria-expanded', 'true');
      }

      function leave() {
        if (!mq.matches) return;
        scheduleClose(details);
      }

      disclosure.addEventListener('mouseenter', open);
      disclosure.addEventListener('mouseleave', leave);

      if (panel) {
        panel.addEventListener('mouseenter', cancelClose);
        panel.addEventListener('mouseleave', leave);
      }
    });

    document.addEventListener('keyup', function (e) {
      if (e.key === 'Escape') {
        cancelClose();
        closeAll(null);
      }
    });
  }

  /* 20260824修正: 商品画像クリックがPCで反応しない不具合対応。
     以前はここでマウスドラッグでのスクロール機能を実装していたが、
     ごくわずかな移動でも「ドラッグ」と誤判定され、通常のクリックまで
     握りつぶしてしまい商品ページに遷移できなくなっていた。
     原因の切り分け後、マウスドラッグ機能自体を撤去し、クリックへの
     影響を完全になくした。PCでの操作は矢印ボタン／トラックパッド／
     Shift+ホイールなどのネイティブなスクロールで行う。 */
  function initCarousels() {
    document.querySelectorAll('.pl-carousel').forEach(function (carousel) {
      var track = carousel.querySelector('.pl-carousel__track');
      if (!track) return;

      var navs = carousel.querySelectorAll('.pl-carousel__nav');
      function toggleNav() {
        var scrollable = track.scrollWidth - track.clientWidth > 2;
        navs.forEach(function (b) {
          b.style.display = scrollable ? '' : 'none';
        });
      }
      toggleNav();
      window.addEventListener('resize', toggleNav);
      window.addEventListener('load', toggleNav);
      window.setTimeout(toggleNav, 200);
      window.setTimeout(toggleNav, 800);
      if (window.ResizeObserver) {
        carousel._plNavObserver = new ResizeObserver(toggleNav);
        carousel._plNavObserver.observe(track);
      }
    });
  }

  /* 20261014追加: 商品カルーセルに「横に続きがある」ことを示す動き。
     カルーセルが画面に半分以上入ったとき、1回だけ商品列を左へ少し
     ずらして戻す（アニメーション本体は custom-theme.css の pl-carousel-nudge）。
     ・IntersectionObserver で監視するのでスクロール中の計算は発生しない
     ・各カルーセルにつき1ページ1回だけ再生
     ・はみ出す商品が無い／既にスクロール済みのカルーセルは動かさない
     ・タップ・クリック・ホイール操作があった時点で即停止
     ・端末の「動きを減らす」設定が有効な場合は動かさない */
  function initCarouselNudge() {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var track = entry.target;
        io.unobserve(track);

        if (track.scrollWidth - track.clientWidth <= 2) return;
        if (track.scrollLeft > 2) return;

        var timer = window.setTimeout(function () {
          track.classList.add('pl-is-nudging');
        }, 250);

        function stop() {
          window.clearTimeout(timer);
          track.classList.remove('pl-is-nudging');
        }

        track.addEventListener('animationend', stop, { once: true });
        ['pointerdown', 'touchstart', 'wheel'].forEach(function (type) {
          track.addEventListener(type, stop, { once: true, passive: true });
        });
      });
    }, { threshold: 0.5 });

    document.querySelectorAll('.pl-carousel__track').forEach(function (track) {
      if (track.getAttribute('data-pl-nudge')) return;
      track.setAttribute('data-pl-nudge', '1');
      io.observe(track);
    });
  }

  function boot() {
    initHeader();
    initSlideshow();
    initNavFit();
    initMegaMenu();
    initCarousels();
    initCarouselNudge();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    window.setTimeout(boot, 0);
  });
})();
